/**
 * Componente de sección de filtros para estadísticas por producto
 * Filters section component for product statistics
 */
import { Search } from '@shared/components/ui/Search';
import { ChangeEvent, useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { ColumnDef } from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBagShopping } from '@fortawesome/pro-light-svg-icons';
import Combobox from '@shared/components/ui/AdvancedFilter/components/Combobox';

interface FiltersSectionProps {
  handleSearch: (value: string) => void;
  onSalesFilterChange?: (value: 'all' | 'sold_last_30_days' | 'not_sold_last_30_days') => void;
  columns: ColumnDef<any>[];
  selectedColumns: string[];
  onColumnsChange: (newSelected: string[]) => void;
  searchTerm: string;
  salesFilter?: 'all' | 'sold_last_30_days' | 'not_sold_last_30_days';
}

export const FiltersSection = ({
  handleSearch,
  onSalesFilterChange,
  columns,
  selectedColumns,
  onColumnsChange,
  searchTerm,
  salesFilter = 'all',
}: FiltersSectionProps) => {
  const { t } = useTranslation('inventoryModule');
  // 🎯 Estado local - NO causa re-render del componente padre
  // Local state - Does NOT cause parent component re-render
  const [inputValue, setInputValue] = useState(searchTerm);

  useEffect(() => {
    setInputValue((prev) => (prev === searchTerm ? prev : searchTerm));
  }, [searchTerm]);

  // 🎯 Opciones del filtro de ventas
  // Sales filter options
  const salesFilterOptions = useMemo(
    () => [
      {
        label: t('filters.sales_filter.all'),
        value: 'all',
      },
      {
        label: t('filters.sales_filter.sold_last_30_days'),
        value: 'sold_last_30_days',
      },
      {
        label: t('filters.sales_filter.not_sold_last_30_days'),
        value: 'not_sold_last_30_days',
      },
    ],
    [t],
  );

  // 🎯 Handler para el cambio del filtro de ventas
  // Handler for sales filter change
  const handleSalesFilterChange = (value: string) => {
    const filterValue = value as 'all' | 'sold_last_30_days' | 'not_sold_last_30_days';
    onSalesFilterChange?.(filterValue);
  };

  // 🎯 Handler para el cambio del input - Ejecuta búsqueda automáticamente (con debounce en el padre)
  // Handler for input change - Executes search automatically (with debounce in parent)
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    // Ejecutar búsqueda automáticamente al escribir (el padre maneja el debounce)
    // Execute search automatically on typing (parent handles debounce)
    handleSearch(newValue);
  };

  // 🎯 Handler para ejecutar búsqueda - comunica al padre el filtro estructurado
  // Handler to execute search - communicates structured filter to parent
  const handleSearchSubmit = () => {
    handleSearch(inputValue.trim());
  };

  return (
    <>
      {/* Campo de búsqueda - Search field */}
      <Search
        placeholder={t('product_statistics.search_placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="product-statistics-search"
        inputDataTestId="product-statistics-search-input"
      />
      {/* Filtro de ventas - Sales filter dropdown */}
      <div className="relative min-w-[200px]">
        {/* Icono de shopping bag - Shopping bag icon (posición absoluta) */}
        <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10 pointer-events-none">
          <FontAwesomeIcon icon={faBagShopping} className="w-3.5 h-3.5 text-icons" />
        </div>
        {/* Combobox con padding izquierdo para el icono - Combobox with left padding for icon */}
        <Combobox
          options={salesFilterOptions}
          placeholder={t('filters.sales_filter.all')}
          value={salesFilter}
          setValue={handleSalesFilterChange}
          className="min-w-[200px]"
          dataTestId="sales-filter-dropdown"
          buttonClassName="!h-8 pl-8"
        />
      </div>
      {/* Selector de columnas - Column selector */}
      <ColumnSelector
        columns={columns}
        selected={selectedColumns}
        onColumnsChange={onColumnsChange}
        storageKey="inventory-product-statistics-table"
      />
    </>
  );
};
